import { Injectable } from '@angular/core';
import { BusLookup } from '@core/model/bus-lookup/bus-lookup';
import {
  BusinessLookup,
  BusinessLookupFilterInput,
  BusinessLookupSortInput,
  GetActBusLookupsByLookupCodeAsParentGQL,
  GetActBusLookupsByLookupCodeGQL,
  GetBusLookupByCodeGQL,
  GetBusLookupByParentModuleCodeAndCategoryGQL,
  GetBusLookupByParentModuleCodeGQL,
  GetBusLookupGQL,
  GetBusLookupsByLookupCodeGQL,
  GetBusLookupsByLookupModuleCodeFilterGQL,
  SortEnumType,
  States,
  UpdateSortOrdersGQL,
  UserFilterInput,
} from '@generated/queries';
import { Apollo, ApolloBase } from 'apollo-angular';
import { firstValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { LocalStorageDataService } from './local-storage.service';
import {
  busLookupsModuleCode,
} from '@core/enums/bus-Lookup';
import { FilterParser } from '@core/filter-parser';
import { ObjectUtils } from '@core/utils/object-utils';
import { FetchPolicy } from '@apollo/client/core';
import {plainToInstance } from 'class-transformer';
import { PageFilter } from '@core/model/pagination/filter';
import { PageResult } from '@core/model/pagination/page-result';

@Injectable({
  providedIn: 'root',
})
export class BusLookupService {
  BusLookupTypes$: Observable<PageResult<BusinessLookup>>;
  BusLookupPriority$: Observable<PageResult<BusinessLookup>>;
  BusLookupStatus$: Observable<PageResult<BusinessLookup>>;
  BusLookupNoteType$: Observable<PageResult<BusinessLookup>>;
  BusLookupProblem$: Observable<PageResult<BusinessLookup>>;
  BusLookupSchedule$: Observable<PageResult<BusinessLookup>>;

  pageFilter: PageFilter = {
    pageIndex: 0,
    activeIndex: 1,
    take: 300,
    search: '',
    state: '',
  };

  private apollo: ApolloBase;
  vmFilters: UserFilterInput = {
    or: [
      {
        firstName: { contains: '' },
      },
      {
        lastName: { contains: '' },
      },
      {
        email: { contains: '' },
      },
    ],
  };

  busFilters: BusinessLookupFilterInput = {
    or: [
      {
        name: { contains: '' },
      },
      {
        code: { contains: '' },
      },
    ],
  };

  constructor(
    private apolloProvider: Apollo,
    private busLookupsByParentCode: GetBusLookupsByLookupCodeGQL,
    private busLookupsByParentCodeAct: GetActBusLookupsByLookupCodeGQL,
    private busLookupsByParentCodeActAsParent: GetActBusLookupsByLookupCodeAsParentGQL,
    private busLookup: GetBusLookupGQL,
    private localStorageService: LocalStorageDataService,
    private getPaginatedBusinessLookupByFilter: GetBusLookupsByLookupModuleCodeFilterGQL,
    private paginatedChildLookups: GetBusLookupsByLookupModuleCodeFilterGQL,
    private getBusLookupByParentModuleCodeGQL: GetBusLookupByParentModuleCodeGQL,
    private getBusLookupByParentModuleCodeAndCatGQL: GetBusLookupByParentModuleCodeAndCategoryGQL,
    private updateSortOrders: UpdateSortOrdersGQL
  ) {
    this.apollo = this.apolloProvider.use('authClient');
  }

  setBusLookup(busLookupId: string) {
    const busLookup = this.busLookup
      .fetch(
        {
          id: busLookupId,
        },
        {
          fetchPolicy: this.localStorageService.getFetchPolicy(
            'busLookup'
          ) as FetchPolicy,
        }
      )
      .pipe(
        map(x => new PageResult<BusLookup>(x.data.paginatedBusinessLookups))
      ); // new PageResult<BusLookup>(x.data.paginatedBusinessLookups)));
    this.BusLookupTypes$ = busLookup;
    this.BusLookupPriority$ = busLookup;
    this.BusLookupNoteType$ = busLookup;
    return busLookup;
  }

  fetchBussinessLookupByCode(event: boolean, lookupCode: string): void {
    if (event) {
      this.pageFilter.search = '';
      this.pageFilter.skip = 0;
    }
    const lookup = this.busLookupsByParentCode
      .fetch({
        search: this.pageFilter.search,
        skip: this.pageFilter.skip,
        take: this.pageFilter.take,
        parentCode: lookupCode,
      })
      .pipe(
        // Use PageResult constructor so `.data` is populated from `.items`/`.nodes`.
        map((x) => new PageResult<BusinessLookup>(x.data.paginatedBusinessLookups))
      );
    if (lookupCode == busLookupsModuleCode.Priority) {
      this.BusLookupPriority$ = lookup;
    } else if (lookupCode == busLookupsModuleCode.TktStatus) {
      this.BusLookupStatus$ = lookup;
    } else if (lookupCode == busLookupsModuleCode.NoteType) {
      this.BusLookupNoteType$ = lookup;
    } else {
      this.BusLookupTypes$ = lookup;
    }
  }

  paginatedBussinessLookupByCode(
    event: boolean,
    lookupCode: string,
  ): Promise<PageResult<BusLookup>> {
    if (event) {
      this.pageFilter.search = '';
      this.pageFilter.skip = 0;
    }
    return firstValueFrom(
      this.busLookupsByParentCode
        .fetch(
          {
            search: this.pageFilter.search,
            skip: this.pageFilter.skip,
            take: this.pageFilter.take,
            parentCode: lookupCode,
          },
          {
            fetchPolicy: this.localStorageService.getFetchPolicy(
              'busLookup'
            ) as FetchPolicy,
          }
        )
        .pipe(
          map((x) => new PageResult<BusLookup>(x.data.paginatedBusinessLookups))
        )
    ); // new PageResult<BusLookup>(x.data.paginatedBusinessLookups))).toPromise();
  }

  paginatedBussinessLookupAllChild(
    event: boolean,
    lookupCode: string,
    policy?: FetchPolicy,
    take?: number
  ): Promise<PageResult<BusLookup>> {
    if (event) {
      this.pageFilter.search = '';
      this.pageFilter.skip = 0;
    }
    return firstValueFrom(
      this.busLookupsByParentCodeAct
        .fetch(
          {
            search: this.pageFilter.search,
            skip: this.pageFilter.skip,
            take: take > 0 ? take : this.pageFilter.take,
            lookupModuleCode: lookupCode,
          },
          {
            fetchPolicy: (policy
              ? policy
              : this.localStorageService.getFetchPolicy(
                  'busLookup'
                )) as FetchPolicy,
          }
        )
        .pipe(
          map((x) =>
            plainToInstance(
              PageResult<BusLookup>,
              x.data.paginatedBusinessLookups
            )
          )
        )
    ); //PageResult<BusLookup>(x.data.paginatedBusinessLookups)) ).toPromise();
  }

  paginatedBussinessLookupByFilter(
    filter: PageFilter,
    lookupCode: string,
    parentId: string = null,
    orderBy: BusinessLookupSortInput,
    policy: string = null,
    state: States | null = null
  ): Promise<PageResult<BusLookup>> {
    this.busFilters = {
      or: [
        {
          name: {
            contains: filter.search.trim(),
          },
        },
        {
          code: {
            contains: filter.search.trim(),
          },
        },
      ],
      and: [
        {
          lookupModuleCode: {
            eq: lookupCode,
          },
        },
        {
          parentId: {
            eq: parentId,
          },
        },
      ],
    };

    const filters = FilterParser.parseFilter({
      state: filter.state,
    });
    if (!ObjectUtils.isObjEmpty(filters)) {
      this.busFilters.and.push(filters);
    }
    const paginationFilter = FilterParser.parsePageFilter(filter);

    // Define default sorting if no orderBy is provided
    const order: BusinessLookupSortInput[] = orderBy
      ? [orderBy]
      : [
          {
            createdOn: SortEnumType.Desc, // Default sorting by name ascending
          },
        ];

    return firstValueFrom(
      this.getPaginatedBusinessLookupByFilter
        .fetch(
          {
            filter: this.busFilters,
            skip: paginationFilter.skip,
            take: paginationFilter.take,
            order,
            state: state ?? undefined,
          },
          {
            fetchPolicy: (policy
              ? policy
              : this.localStorageService.getFetchPolicy(
                  'busLookup'
                )) as FetchPolicy,
          }
        )
        .pipe(
          map(
            (x) =>
              //eslint-disable-next-line
              new PageResult<BusLookup>(x.data.paginatedBusinessLookups as any)
          )
        )
    );
  }

  paginatedBussinessLookupCategoryByFilter(
    filter: PageFilter,
    lookupCode: string,
    parentId: string = null,
    orderBy:BusinessLookupSortInput,
    policy: string = null
  ): Promise<PageResult<BusLookup>> {
    this.busFilters = {
      or: [
        {
          name: {
            contains: filter.search.trim(),
          },
        },
        {
          code: {
            contains: filter.search.trim(),
          },
        },
      ],
      and: [
        {
          lookupCategory: {
            eq: lookupCode,
          },
        },
        {
          parentId: {
            eq: parentId,
          },
        },
      ],
    };

    const filters = FilterParser.parseFilter({
      state: filter.state,
    });
    if (!ObjectUtils.isObjEmpty(filters)) {
      this.busFilters.and.push(filters);
    }
    const paginationFilter = FilterParser.parsePageFilter(filter);

    // Define default sorting if no orderBy is provided
    const order: BusinessLookupSortInput[] = orderBy
      ? [orderBy]
      : [
          {
            name: SortEnumType.Asc, // Default sorting by name ascending
          },
        ];

    return firstValueFrom(
      this.getPaginatedBusinessLookupByFilter
        .fetch(
          {
            filter: this.busFilters,
            skip: paginationFilter.skip,
            take: paginationFilter.take,
            order
          },
          {
            fetchPolicy: (policy
              ? policy
              : this.localStorageService.getFetchPolicy(
                  'busLookup'
                )) as FetchPolicy,
          }
        )
        .pipe(
          map(
            (x) =>
              //eslint-disable-next-line
              new PageResult<BusLookup>(x.data.paginatedBusinessLookups as any)
          )
        )
    );
  }

  getLookupDataByParentModuleCode(
    moduleCode: string,
    fetchPolicy?: FetchPolicy
  ): Promise<BusLookup[]> {
    return firstValueFrom(
      this.getBusLookupByParentModuleCodeGQL
        .fetch(
          {
            moduleCode,
          },
          {
            fetchPolicy:
              fetchPolicy ??
              this.localStorageService.getFetchPolicy('busLookup'),
          }
        )
        .pipe(
          map((x) => {
            // Map the fetched data and filter childSite based on state = ACTIVE
            return plainToInstance(
              BusLookup,
              x.data.businessLookups.map((lookup) => ({
                ...lookup,
                childLookup: lookup.childLookup.filter(
                  (child) => child.state === 'ACTIVE'
                ),
              }))
            );
          })
        )
    );
  }

  getLookupDataByParentModuleCodeAndCategory(
    moduleCode: string,
    categoryCode:string,
    fetchPolicy?: FetchPolicy
  ): Promise<BusLookup[]> {
    return firstValueFrom(
      this.getBusLookupByParentModuleCodeAndCatGQL
        .fetch(
          {
            moduleCode,
            categoryCode
          },
          {
            fetchPolicy:
              fetchPolicy ??
              this.localStorageService.getFetchPolicy('busLookup'),
          }
        )
        .pipe(
          map((x) => {
            // Map the fetched data and filter childSite based on state = ACTIVE
            return plainToInstance(
              BusLookup,
              x.data.businessLookups.map((lookup) => ({
                ...lookup,
                childLookup: lookup.childLookup.filter(
                  (child) => child.state === 'ACTIVE'
                ),
              }))
            );
          })
        )
    );
  }

  paginatedBussinessLookup(
    filter: PageFilter,
    lookupCode: string,
    policy: FetchPolicy = null,
   //eslint-disable-next-line
    takeRecord?: any
  ): Promise<PageResult<BusLookup>> {
    this.busFilters = {
      or: [
        {
          name: {
            contains: filter.search.trim(),
          },
        },
        {
          code: {
            contains: filter.search.trim(),
          },
        },
        { childLookup: { some: { name: { contains: filter.search.trim() } } } }
      ],
      and: [
        {
          lookupModuleCode: {
            eq: lookupCode,
          },
        },
      ],
    };

    const filters = FilterParser.parseFilter({
      state: filter.state,
    });
    if (!ObjectUtils.isObjEmpty(filters)) {
      this.busFilters.and.push(filters);
    }
    const paginationFilter = FilterParser.parsePageFilter(filter);
    return firstValueFrom(
      this.getPaginatedBusinessLookupByFilter
        .fetch(
          {
            filter: this.busFilters,
            skip: paginationFilter.skip,
            take: takeRecord ? takeRecord : paginationFilter.take,
          },
          {
            fetchPolicy: (policy
              ? policy
              : this.localStorageService.getFetchPolicy(
                  'busLookup'
                )) as FetchPolicy,
          }
        )
        .pipe(
          map(
            (x) =>
              new PageResult<BusLookup>(
                //eslint-disable-next-line
                x.data.paginatedBusinessLookups as any
              )
          )
        )
    );
  }

  paginatedBussinessLookupByModuleCodeAndCategory(
    filter: PageFilter,
    moduleCode: string,
    categoryCode: string,
    policy: FetchPolicy = null,
    //eslint-disable-next-line
    takeRecord?: any,
    orderBy: BusinessLookupSortInput = { createdOn: SortEnumType.Desc }
  ): Promise<PageResult<BusLookup>> {
    this.busFilters = {
      or: [
        { name: { contains: filter.search.trim() } },
        { code: { contains: filter.search.trim() } },
        { childLookup: { some: { name: { contains: filter.search.trim() } } } },
      ],
      and: [
        { lookupModuleCode: { eq: moduleCode } },
        { lookupCategory: { eq: categoryCode } },
      ],
    };

    // NOTE: caller controls `filter.state`. If empty, both ACTIVE + INACTIVE will be returned.
    const stateFilter = FilterParser.parseFilter({ state: filter.state });
    if (!ObjectUtils.isObjEmpty(stateFilter)) {
      this.busFilters.and.push(stateFilter);
    }

    const paginationFilter = FilterParser.parsePageFilter(filter);
    const order: BusinessLookupSortInput[] = orderBy ? [orderBy] : [];

    return firstValueFrom(
      this.getPaginatedBusinessLookupByFilter
        .fetch(
          {
            filter: this.busFilters,
            skip: paginationFilter.skip,
            take: takeRecord ? takeRecord : paginationFilter.take,
            order,
          },
          {
            fetchPolicy: (policy
              ? policy
              : this.localStorageService.getFetchPolicy('busLookup')) as FetchPolicy,
          }
        )
        .pipe(
          map(
            (x) =>
              new PageResult<BusLookup>(
                //eslint-disable-next-line
                x.data.paginatedBusinessLookups as any
              )
          )
        )
    );
  }

  paginatedBussinessLookupOnlyChild(
    filter: PageFilter,
    lookupCode: string,
    policy: FetchPolicy = null,
    //eslint-disable-next-line
    takeRecord?: any
  ): Promise<PageResult<BusLookup>> {
    this.busFilters = {
      or: [
        {
          name: {
            contains: filter.search.trim(),
          },
        },
        {
          code: {
            contains: filter.search.trim(),
          },
        },
      ],
      and: [
        {
          lookupModuleCode: {
            eq: lookupCode,
          },
        },
        {
          parentId: {
            neq: null,
          },
        },
      ],
    };

    const filters = FilterParser.parseFilter({
      state: filter.state,
    });
    if (!ObjectUtils.isObjEmpty(filters)) {
      this.busFilters.and.push(filters);
    }
    const paginationFilter = FilterParser.parsePageFilter(filter);
    return firstValueFrom(
      this.getPaginatedBusinessLookupByFilter
        .fetch(
          {
            filter: this.busFilters,
            skip: paginationFilter.skip,
            take: takeRecord ? takeRecord : paginationFilter.take,
          },
          {
            fetchPolicy: (policy
              ? policy
              : this.localStorageService.getFetchPolicy(
                  'busLookup'
                )) as FetchPolicy,
          }
        )
        .pipe(
          map((x) =>
            plainToInstance(
              PageResult<BusLookup>,
              x.data.paginatedBusinessLookups
            )
          )
        )
    );
  }

  fetchNextBussinessLookupByCode(event: number, lookupCode: string): void {
    this.pageFilter.skip = event;
    this.fetchBussinessLookupByCode(false, lookupCode);
  }


  //eslint-disable-next-line
  searchInBussinessLookupByCode(event: any, lookupCode: string): void {
    this.pageFilter.search = event.term;
    this.pageFilter.skip = null;
    this.fetchBussinessLookupByCode(false, lookupCode);
  }

  fetchBussinessLookupByCodeActAllChild(
    event: boolean,
    lookupModuleCode: string
  ): void {
    if (event) {
      this.pageFilter.search = '';
      this.pageFilter.skip = 0;
    }
    const lookup = this.busLookupsByParentCodeAct
      .fetch(
        {
          search: this.pageFilter.search,
          skip: this.pageFilter.skip,
          take: this.pageFilter.take,
          lookupModuleCode: lookupModuleCode,
        },
        {
          fetchPolicy: 'network-only',
        }
      )
      .pipe(
        map(
          (x) =>
            new PageResult<BusinessLookup>(
             //eslint-disable-next-line
              x.data.paginatedBusinessLookups as any
            )
        )
      );

      if(lookupModuleCode == busLookupsModuleCode.Priority){
        this.BusLookupPriority$ = lookup;
      }
      else if(lookupModuleCode == busLookupsModuleCode.TktStatus){
        this.BusLookupStatus$ = lookup;
      }
      else if(lookupModuleCode == busLookupsModuleCode.NoteType){
        this.BusLookupNoteType$ = lookup;
      }
      else if(lookupModuleCode == busLookupsModuleCode.Problem){
        this.BusLookupProblem$ = lookup;
      }
      else if(lookupModuleCode == busLookupsModuleCode.TicketSchedule){
        this.BusLookupSchedule$ = lookup;
      }
      else{
        this.BusLookupTypes$ = lookup;
      }

  }

  fetchNextBussinessLookupByCodeAct(event: number,lookupCode:string): void {
    this.pageFilter.skip = event;
    this.fetchBussinessLookupByCodeActAllChild(false,lookupCode);
  }


  //eslint-disable-next-line
  searchInBussinessLookupByCodeAct(event: any,lookupCode:string): void {
    this.pageFilter.search = event.term;
    this.pageFilter.skip = null;
    this.fetchBussinessLookupByCodeActAllChild(false,lookupCode);
  }

 fetchBussinessLookupByCodeActAsParent(
    event: boolean,
    lookupModuleCode: string,
    lookupId?: string,
    filter?: PageFilter,
  ): void {

    const pageFilter = filter ?? this.pageFilter;
    if (event) {
      pageFilter.search = '';
      pageFilter.skip = 0;
    }


    //eslint-disable-next-line
    const variables: any = {
      search: pageFilter.search,
          skip: pageFilter.skip,
          take: pageFilter.take,
      lookupModuleCode: lookupModuleCode,
    };

    if (lookupId) {
      variables.lookupId = lookupId;
    } else {
      variables.lookupId = '00000000-0000-0000-0000-000000000000'; // Safe dummy GUID
    }

    const lookup = this.busLookupsByParentCodeActAsParent
      .fetch(variables, {
        fetchPolicy: 'network-only',
      })
      .pipe(
        map(
          (x) =>
            new PageResult<BusinessLookup>(
             //eslint-disable-next-line
              x.data.paginatedBusinessLookups as any
            )
        )
      );
    if (lookupModuleCode == busLookupsModuleCode.Priority) {
      this.BusLookupPriority$ = lookup;
    } else if (lookupModuleCode == busLookupsModuleCode.TktStatus) {
      this.BusLookupStatus$ = lookup;
    } else if (lookupModuleCode == busLookupsModuleCode.NoteType) {
      this.BusLookupNoteType$ = lookup;
    } else if (lookupModuleCode == busLookupsModuleCode.TicketSchedule) {
      this.BusLookupSchedule$ = lookup;
    } else {
      this.BusLookupTypes$ = lookup;
    }
  }

  fetchNextBussinessLookupByCodeActAsParent(
    event: number,
    lookupModuleCode: string
  ): void {
    this.pageFilter.skip = event;
    this.fetchBussinessLookupByCodeActAsParent(false, lookupModuleCode);
  }

  searchInBussinessLookupByCodeActAsParent(
    //eslint-disable-next-line
    event: any,
    lookupModuleCode: string
  ): void {
    this.pageFilter.search = event;
    this.pageFilter.skip = null;
    this.fetchBussinessLookupByCodeActAsParent(false, lookupModuleCode);
  }

  updateBusLookupSortOrders(items: { id: string; sortKey: number }[]) {
    ;
    return firstValueFrom(
      this.updateSortOrders.mutate({items}
      ).
      pipe(
        (map((x) => x.data?.business?.busLookup?.updateSortOrder))
      )
    )
  }

  /**
   * Checks if a note-type name or code already exists for the same module, category, and parent.
   * Uses existing paginated lookup query (no new API).
   */
  findBusinessLookupDuplicate(
    moduleCode: string,
    categoryCode: string,
    parentId: string | null,
    criteria: { name?: string; code?: string },
    excludeId?: string | null,
    policy: FetchPolicy = 'network-only'
  ): Promise<BusLookup | null> {
    const name = criteria.name?.trim();
    const code = criteria.code?.trim().toUpperCase();
    if (!name && !code) return Promise.resolve(null);

    const and: BusinessLookupFilterInput[] = [
      { lookupModuleCode: { eq: moduleCode } },
      { lookupCategory: { eq: categoryCode } },
      { parentId: { eq: parentId ?? null } },
    ];

    if (name) {
      and.push({ name: { eq: name } });
    } else if (code) {
      and.push({ code: { eq: code } });
    }

    const filter: BusinessLookupFilterInput = { and };

    return firstValueFrom(
      this.getPaginatedBusinessLookupByFilter.fetch(
        {
          filter,
          skip: 0,
          take: 10,
          order: [{ createdOn: SortEnumType.Desc }],
        },
        { fetchPolicy: policy }
      )
    ).then((x) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const items = ((x.data as any)?.paginatedBusinessLookups?.items ??
        []) as BusLookup[];

      return (
        items.find((item) => {
          if (excludeId && item.id === excludeId) return false;
          if (name) {
            return (item.name ?? '').trim().toLowerCase() === name.toLowerCase();
          }
          if (code) {
            return (item.code ?? '').trim().toUpperCase() === code;
          }
          return false;
        }) ?? null
      );
    });
  }

  getBusChildLookupsByParentId(
    filter: PageFilter,
    orderBy:BusinessLookupSortInput,
    parentId: string = null,
  ) {
    return firstValueFrom(
      this.paginatedChildLookups.fetch(
        {
          skip: filter.pageIndex && filter.take ? filter.pageIndex * filter.take : 0,
          take: filter.take,
          filter: {
          or: [
            {
              code: {
                contains: filter.search?.trim()
              }
            },
            {
              name: {
                contains: filter.search?.trim()
              }
            },
            {
              childLookup: {
                some: {
                  or: [
                    { name: { contains: filter.search?.trim() } },
                    { code: { contains: filter.search?.trim() } }
                  ]
                }
              }
            }
          ],
          and: [
        {
          parentId: {
            eq: parentId,
          },
        },
        ...(filter.state !== '' ? [{ state: { eq: filter.state as States } }] : []),
      ],

        },
        order: orderBy
        },
        { fetchPolicy : 'network-only' }
      )
      .pipe(
        map((x)=> new PageResult<BusLookup>(x.data.paginatedBusinessLookups))
      )
    )

  }
}

